import React, { useState } from "react";
import {
  Box, Card, CardContent, Typography, Button, TextField, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, Switch,
  Tooltip, InputAdornment,
} from "@mui/material";
import { Plus, Pencil, Search, Inbox } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import AdminShell from "../../admin/layout/AdminShell";

/**
 * Generic list + create/edit dialog for simple admin CRUD entities.
 * See BusinessOfferingsPage.jsx etc. for usage examples.
 */
export default function GenericCrudPage({
  title, queryKey, listFn, createFn, updateFn,
  columns, formFields, getId = (row) => row.id, emptyItem = {},
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create mode
  const [form, setForm] = useState(emptyItem);

  const { data, isLoading } = useQuery({
    queryKey: [queryKey, search],
    queryFn: () => listFn(search ? { search } : {}).then((r) => r.data),
  });

  const rows = Array.isArray(data) ? data : data?.results ?? [];

  const saveMutation = useMutation({
    mutationFn: () => (editing ? updateFn(getId(editing), form) : createFn(form)),
    onSuccess: () => {
      qc.invalidateQueries([queryKey]);
      toast.success(editing ? "Updated" : "Created");
      setDialogOpen(false);
    },
    onError: (err) =>
      toast.error(err?.response?.data ? JSON.stringify(err.response.data) : "Save failed"),
  });

  const openCreate = () => { setEditing(null); setForm(emptyItem); setDialogOpen(true); };
const openEdit = (row) => {
    setEditing(row);
    // Flatten any nested {id, name, ...} objects (e.g. product.category, product.vendor)
    // down to just their id, since form selects and the write payload both expect a plain PK.
    const flattened = Object.fromEntries(
      Object.entries(row).map(([k, v]) => [k, v && typeof v === "object" && !Array.isArray(v) && "id" in v ? v.id : v])
    );
    setForm({ ...emptyItem, ...flattened });
    setDialogOpen(true);
  };
    const handleField = (name, value) => setForm((f) => ({ ...f, [name]: value }));

  return (
    <AdminShell title={title}>
      <Card sx={{ mb: 2, borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <CardContent sx={{ p: 2.5, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            placeholder="Search..."
            size="small"
            sx={{ minWidth: 240 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} color="#94A3B8" /></InputAdornment> }}
          />
          <Button
            variant="contained" size="small" startIcon={<Plus size={15} />}
            onClick={openCreate}
            sx={{ ml: "auto", textTransform: "none", fontWeight: 700, borderRadius: "10px", bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" } }}
          >
            Add New
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {columns.map((c) => (
                  <TableCell key={c.key} sx={{ fontWeight: 700, fontSize: 12.5, color: "#475569", whiteSpace: "nowrap" }}>
                    {c.label}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={columns.length + 1} align="center" sx={{ py: 6 }}><CircularProgress size={26} /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 7, color: "text.secondary" }}>
                    <Inbox size={28} style={{ opacity: 0.35, marginBottom: 8 }} />
                    <Typography fontSize={14}>No records found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={getId(row)} sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    {columns.map((c) => (
                      <TableCell key={c.key} sx={{ fontSize: 13.5 }}>
                        {c.render ? c.render(row) : row[c.key]}
                      </TableCell>
                    ))}
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <Pencil size={15} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editing ? "Edit" : "Add New"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1.5 }}>
          {formFields.map((f) => {
            if (f.type === "switch") {
              return (
                <Box key={f.name} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={{ fontSize: 13.5 }}>{f.label}</Typography>
                  <Switch checked={!!form[f.name]} onChange={(e) => handleField(f.name, e.target.checked)} />
                </Box>
              );
            }
            if (f.type === "select") {
              return (
                <TextField
                  key={f.name} select size="small" label={f.label} required={f.required}
                  value={form[f.name] ?? ""}
                  onChange={(e) => handleField(f.name, e.target.value)}
                >
                  {(f.options || []).map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </TextField>
              );
            }
            if (f.type === "file") {
              const current = form[f.name];
              return (
                <Box key={f.name} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "text.secondary" }}>{f.label}</Typography>
                  <Button variant="outlined" component="label" size="small" sx={{ textTransform: "none", alignSelf: "flex-start" }}>
                    {current instanceof File ? current.name : "Choose file"}
                    <input type="file" hidden accept="image/*" onChange={(e) => handleField(f.name, e.target.files[0])} />
                  </Button>
                  {typeof current === "string" && current && (
                    <Box component="img" src={current} alt="" sx={{ height: 44, width: "auto", borderRadius: 1 }} />
                  )}
                </Box>
              );
            }
            return (
              <TextField
                key={f.name}
                size="small"
                label={f.label}
                required={f.required}
                type={f.type === "number" ? "number" : "text"}
                multiline={f.type === "textarea"}
                rows={f.type === "textarea" ? 3 : undefined}
                value={form[f.name] ?? ""}
                onChange={(e) => handleField(f.name, e.target.value)}
              />
            );
          })}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>
            Cancel
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            variant="contained"
            disabled={saveMutation.isPending}
            sx={{ textTransform: "none", fontWeight: 700, bgcolor: "#2563EB", "&:hover": { bgcolor: "#1D4ED8" } }}
          >
            {saveMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminShell>
  );
}